import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import HeadMeta from '../components/HeadMeta';
import ScrollReveal from '../components/ScrollReveal';
import { useScrollZoom } from '../hooks/useScrollZoom';

export default function AboutUs() {
  const { lang, t } = useLanguage();
  const heroZoom = useScrollZoom({ baseScale: 1.1, maxScale: 1.4, zoomSpeed: 0.0006 });

  return (
    <>
      <HeadMeta 
        title={t('aboutPage.metaTitle') || 'Om os - Grundlagt af Nicoline og Ziad'} 
        description={t('aboutPage.metaDesc') || 'Historien om Nicoline og Ziad. Captain Hurghada Tours kombinerer personlig tryghed og service med ægte egyptisk lokalkendskab.'}
      />

      {/* Hero */}
      <section className="pt-28 sm:pt-36 lg:pt-40 pb-16 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-40 overflow-hidden">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/086b06dd-92e0-474f-8c57-ea0b72c8dd5a/roedehavet-badebro.webp"
            alt="Captain Hurghada Tours Rødehavet"
            style={{
              transform: `scale(${heroZoom.scale}) translate3d(0, ${heroZoom.translateY}px, 0)`,
              transition: 'transform 0.1s cubic-bezier(0.1, 1, 0.1, 1)',
              willChange: 'transform',
            }}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent pointer-events-none" />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <ScrollReveal variant="up" duration={800}>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-6 uppercase">
              {t('aboutPage.heroTitle')}
            </h1>
          </ScrollReveal>

          <ScrollReveal variant="up" delay={200} duration={800}>
            <p className="text-sm sm:text-base text-slate-300 max-w-3xl mx-auto leading-relaxed">
              {t('aboutPage.heroSubtitle')}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Main Story Section */}
      <section className="py-20 bg-white relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start mb-16">
            
            {/* Left Column: Nicoline og Ziad Image (Portrait) */}
            <div className="lg:col-span-5 relative">
              <ScrollReveal variant="up" duration={800}>
                <div className="rounded-3xl overflow-hidden shadow-xl border border-slate-200 bg-white aspect-[3/4] max-w-md mx-auto lg:max-w-none">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/086b06dd-92e0-474f-8c57-ea0b72c8dd5a/nicoline-og-mand.webp"
                    alt="Nicoline og hendes egyptiske mand"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="bg-white text-slate-900 p-6 rounded-2xl shadow-md border border-slate-200 mt-6 max-w-md mx-auto lg:max-w-none">
                  <p className="font-bold text-base sm:text-lg leading-snug text-slate-900 italic">
                    {t('aboutPage.quote')}
                  </p>
                  <p className="text-xs text-slate-500 mt-2 font-semibold">
                    {t('aboutPage.founderRole')}
                  </p>
                </div>
              </ScrollReveal>
            </div>

            {/* Right Column: Exact Story Copy */}
            <div className="lg:col-span-7 space-y-6">
              <ScrollReveal variant="up" duration={800} delay={150}>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight uppercase tracking-tight">
                  {t('aboutPage.storyHeading')}
                </h2>

                <div className="prose prose-slate max-w-none text-slate-700 space-y-4 text-sm sm:text-base leading-relaxed pt-2">
                  <p>
                    {t('aboutPage.storyP1')}
                  </p>
                  <p>
                    {t('aboutPage.storyP2')}
                  </p>
                  <p>
                    {t('aboutPage.storyP3')}
                  </p>
                  <p>
                    {t('aboutPage.storyP4')}
                  </p>
                  <p>
                    {t('aboutPage.storyP5')}
                  </p>
                  <p>
                    {t('aboutPage.storyP6')}
                  </p>
                  <p className="font-bold text-slate-900 pt-2">
                    {t('aboutPage.storyP7')}
                  </p>
                  <p className="font-bold text-slate-900 text-base sm:text-lg">
                    {t('aboutPage.storyP8')}
                  </p>
                </div>

                {/* Core Values */}
                <div className="grid grid-cols-2 gap-y-4 gap-x-8 pt-6 border-t border-slate-100 mt-8">
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-heart text-cyan-600 text-lg shrink-0" aria-hidden="true" />
                    <span className="font-semibold text-slate-800 text-sm">
                      {t('aboutPage.values.passion')}
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-shield-halved text-cyan-600 text-lg shrink-0" aria-hidden="true" />
                    <span className="font-semibold text-slate-800 text-sm">
                      {t('aboutPage.values.safety')}
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-compass text-cyan-600 text-lg shrink-0" aria-hidden="true" />
                    <span className="font-semibold text-slate-800 text-sm">
                      {t('aboutPage.values.planning')}
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-user-check text-cyan-600 text-lg shrink-0" aria-hidden="true" />
                    <span className="font-semibold text-slate-800 text-sm">
                      {t('aboutPage.values.quality')}
                    </span>
                  </div>
                </div>

              </ScrollReveal>
            </div>

          </div>

          {/* Social Proof / Community Callout Banner (Matching Live Site) */}
          <ScrollReveal variant="up" duration={800} delay={200}>
            <div className="max-w-5xl mx-auto my-12 p-8 sm:p-10 rounded-3xl border border-slate-200 bg-white shadow-sm flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
              <div className="max-w-xl">
                <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 uppercase tracking-tight mb-2">
                  {lang === 'da' ? 'Vil du følge med i vores hverdag?' : lang === 'de' ? 'Möchten Sie unseren Alltag begleiten?' : lang === 'no' ? 'Vil du følge med i hverdagen vår?' : lang === 'sv' ? 'Vill du följa med i vår vardag?' : 'Want to follow our daily adventures?'}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {lang === 'da' ? 'Vi deler dagligt videoer fra vores bådture, dyk og ørkensafarier på Instagram @captainhurgada' : lang === 'de' ? 'Wir teilen täglich Videos von unseren Bootstouren, Tauchgängen und Wüstensafaris auf Instagram @captainhurgada' : lang === 'no' ? 'Vi deler daglig videoer fra våre båtturer, dykking og ørkensafarier på Instagram @captainhurgada' : lang === 'sv' ? 'Vi delar dagligen videor från våra båtturer, dyk och ökensafarier på Instagram @captainhurgada' : 'We share daily videos from our boat trips, diving, and desert safaris on Instagram @captainhurgada'}
                </p>
              </div>

              <a
                href="https://instagram.com/captainhurgada"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2.5 px-8 py-3.5 rounded-full bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-sm tracking-wide transition-all shadow-sm shrink-0"
              >
                <i className="fa-brands fa-instagram text-lg" aria-hidden="true" />
                <span>@captainhurgada {lang === 'da' ? 'på Instagram' : lang === 'de' ? 'auf Instagram' : lang === 'no' ? 'på Instagram' : lang === 'sv' ? 'på Instagram' : 'on Instagram'}</span>
              </a>
            </div>
          </ScrollReveal>

        </div>
      </section>
    </>
  );
}
